import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  Stack,
} from "@mui/material";
import { useUserAuth } from "./UserAuthContext";

function formatMinutes(totalMinutes) {
  const minutes = Number.isFinite(totalMinutes) ? totalMinutes : 0;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (hours <= 0) {
    return `${minutes} min`;
  }
  return `${hours}h ${rem}m`;
}

function getDefaultRange() {
  const today = new Date();
  const to = today.toISOString().slice(0, 10); // YYYY-MM-DD

  const fromDate = new Date(today);
  fromDate.setDate(fromDate.getDate() - 6);
  const from = fromDate.toISOString().slice(0, 10);

  return { from, to };
}

const MyTimePage = () => {
  const { token, isAuthenticated, loading: authLoading } = useUserAuth();

  const defaultRange = useMemo(() => getDefaultRange(), []);
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);

  const [data, setData] = useState({ days: [], total_minutes: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchSummary = async () => {
    if (!token || !isAuthenticated) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("from", fromDate);
      if (toDate) params.append("to", toDate);

      const res = await fetch(`/api/time/my-summary?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json && json.error ? json.error : "Failed to load time summary");
      }

      setData({
        days: json.days || [],
        total_minutes:
          typeof json.total_minutes === "number" ? json.total_minutes : 0,
      });
    } catch (err) {
      console.error("Failed to load time summary:", err);
      setError(err.message || "Failed to load time summary");
    } finally {
      setLoading(false);
    }
  };

  // Load on initial mount once auth is ready
  useEffect(() => {
    if (!authLoading && isAuthenticated && token) {
      fetchSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, token]);

  const applyLast7Days = () => {
    const range = getDefaultRange();
    setFromDate(range.from);
    setToDate(range.to);
  };

  const applyThisWeek = () => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun .. 6=Sat
    const diffToMonday = (day + 6) % 7; // number of days since Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() - diffToMonday);

    const from = monday.toISOString().slice(0, 10);
    const to = now.toISOString().slice(0, 10);

    setFromDate(from);
    setToDate(to);
  };

  if (authLoading) {
    return <Typography>Loading user...</Typography>;
  }

  if (!isAuthenticated) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <Card sx={{ maxWidth: 500, width: "100%" }}>
          <CardHeader title="My Time" />
          <CardContent>
            <Alert severity="info">
              Please log in via the <strong>User Login</strong> panel to view your time
              summary.
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box mt={2} display="flex" justifyContent="center">
      <Card sx={{ maxWidth: 800, width: "100%" }}>
        <CardHeader title="My Time" />
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
            <TextField
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            mb={2}
          >
            <Button variant="outlined" onClick={applyLast7Days}>
              Last 7 days
            </Button>
            <Button variant="outlined" onClick={applyThisWeek}>
              This week
            </Button>
            <Box flexGrow={1} />
            <Button
              variant="contained"
              onClick={fetchSummary}
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box mb={2}>
            <Typography variant="subtitle1">
              Total time in range:{" "}
              <strong>{formatMinutes(data.total_minutes)}</strong>
            </Typography>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Minutes</TableCell>
                <TableCell align="right">Approx. Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.days && data.days.length > 0 ? (
                data.days.map((day) => (
                  <TableRow key={day.date}>
                    <TableCell>{day.date}</TableCell>
                    <TableCell align="right">{day.minutes}</TableCell>
                    <TableCell align="right">
                      {formatMinutes(day.minutes)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2">
                      No recorded time in the selected range.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MyTimePage;
